import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  TextInput,
  Datepicker,
} from "flowbite-react";
import { useEffect, useState } from "react";
import { SquareX, Trash2 } from "lucide-react";
import axiosInstance from "../config/config";

export function ExpensesModal({ closeModal, job }) {
  const [expenses, setExpenses] = useState([]);
  const [exp_title, setExpTitle] = useState("");
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [e_date, setEDate] = useState(new Date());
  const [loading, setLoading] = useState(false);

  const fetchExpenses = async () => {
    try {
      const response = await axiosInstance.post("", {
        SysID: `select * from dbo.job_expense where job_id=${job.id} order by e_date desc;`,
      });
      setExpenses(response.data || []);
    } catch (error) {
      console.error("Error fetching expenses:", error);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const totalAmount = expenses.reduce(
    (sum, e) => sum + parseFloat(e.amount || 0),
    0
  );

  const handleAdd = async () => {
    if (!exp_title.trim() || !amount) return;
    setLoading(true);
    try {
      const dateStr =
        e_date instanceof Date
          ? e_date.toISOString().split("T")[0]
          : e_date;
      await axiosInstance.post("", {
        SysID: `EXEC dbo.add_job_expense @job_id=${job.id}, @exp_title='${exp_title}', @note='${note}', @amount=${amount}, @e_date='${dateStr}'; SELECT 1`,
      });
      setExpTitle("");
      setNote("");
      setAmount("");
      setEDate(new Date());
      fetchExpenses();
    } catch (error) {
      console.error("Error adding expense:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.post("", {
        SysID: `EXEC dbo.del_job_expense @id=${id}; SELECT 1`,
      });
      fetchExpenses();
    } catch (error) {
      console.error("Error deleting expense:", error);
    }
  };

  return (
    <Modal show={true} size="lg" onClose={closeModal} popup>
      <ModalHeader />
      <ModalBody>
        <div className="space-y-4">
          <div className="flex items-start">
            <div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                Expenses — {job.j_name}
              </h3>
              <p className="text-sm text-gray-500">{job.c_name}</p>
            </div>
            <SquareX
              onClick={closeModal}
              className="ml-auto text-red-600 hover:cursor-pointer"
            />
          </div>

          {/* Total */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Total Expenses
            </span>
            <span className="text-2xl font-bold text-blue-600">
              Rs. {totalAmount.toFixed(2)}
            </span>
          </div>

          {/* Expense list */}
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {expenses.length === 0 && (
              <p className="text-gray-400 text-sm text-center py-4">
                No expenses yet.
              </p>
            )}
            {expenses.map((exp) => (
              <div
                key={exp.id}
                className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white text-sm">
                      {exp.exp_title}
                    </span>
                    <span className="text-xs text-gray-400">
                      {exp.e_date?.split("T")[0]}
                    </span>
                  </div>
                  {exp.note && (
                    <p className="text-xs text-gray-500 mt-0.5">{exp.note}</p>
                  )}
                </div>
                <span className="font-semibold text-gray-900 dark:text-white text-sm whitespace-nowrap">
                  Rs. {parseFloat(exp.amount).toFixed(2)}
                </span>
                <button
                  onClick={() => handleDelete(exp.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          {/* Add expense */}
          <div className="border-t pt-4 space-y-3">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Add Expense
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs mb-1 block">Title *</Label>
                <TextInput
                  value={exp_title}
                  onChange={(e) => setExpTitle(e.target.value)}
                  placeholder="Expense title"
                  sizing="sm"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Amount (Rs.) *</Label>
                <TextInput
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  sizing="sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs mb-1 block">Note</Label>
                <TextInput
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note"
                  sizing="sm"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Date</Label>
                <Datepicker
                  value={e_date}
                  onChange={(d) => setEDate(d)}
                  weekStart={1}
                  inline={false}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleAdd} disabled={loading} size="sm">
                Add Expense
              </Button>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}
